/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import Loader from 'components/atoms/loader';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { BiddingState } from 'store/modules/bidding_store/types';
import { OnlineSearchState, OnlineSearchTypes } from 'store/modules/online_search_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import ReCAPTCHA from 'react-google-recaptcha';
import EmptyResult from 'components/atoms/empty_result';
import PerPageOptions from 'components/atoms/per_page_options';
import TotalEntriesLabel from 'components/atoms/total_entries_label';
import SelectButton from 'components/atoms/select_button';
import ItemTools from 'components/molecules/item_tools';
import { getDownloadBiddings } from 'services/actions/bidding_actions';
import ItemShare from 'components/atoms/item_share';
import Pagination from 'components/atoms/pagination';
import InAnalysisAlert from 'components/molecules/in_analysis_alert';
import createTrack from 'services/actions/tracker_actions';
import { toggleSelected } from 'services/toggleSelected';
import { useReactToPrint } from 'react-to-print';
import { CustomSearches, getCustomSearch } from 'services/actions/custom_search';
import CustomSearchesItem from 'components/atoms/custom_searches_item';
import { destroyFavoriteAll } from 'store/modules/bidding_store/actions';
import { showUndoToast } from 'store/modules/undo_toast_store/actions';
import BatchConfirmModal from 'components/atoms/batch_confirm_modal';
import AffectedMembersSelect from 'components/atoms/affected_members_select';
import { getManagementMembers } from 'store/modules/management_store/actions';
import tabId from 'services/tabId';
import { ManagementState } from 'store/modules/management_store/types';
import companyId from 'services/company_id';
import BiddingItemNew from 'components/molecules/bidding_item_new';
import OpenTutorial from 'components/atoms/open_tutorial';
import Select from 'react-select';
import { Container } from './styles';

interface BiddingListProps {
  searchBarOpened: boolean;
  fromManagement?: boolean;
}

interface OrderOptionType {
  value: string;
  label: string;
}

const ORDER_FIELD_OPTIONS: OrderOptionType[] = [
  { value: 'modified', label: 'Atualização' },
  { value: 'validade', label: 'Datas' },
];

const NO_MATCH_MESSAGE = 'Nenhum item dos membros escolhidos está entre os selecionados.';

const BiddingList = (props: BiddingListProps) => {
  const { searchBarOpened, fromManagement = false } = props;
  const dispatch = useDispatch();
  const { biddings, request } = useSelector<ApplicationState, BiddingState>(
    (state) => state.session.biddings,
  );

  const { management, request: managementRequest } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { onlineSearch } = useSelector<ApplicationState, OnlineSearchState>(
    (state) => state.session.onlineSearch,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isCompanyView = management?.filtered_by === 'client';
  const canShowDeleteButton = isCompanyView ? !!profile.consultant_login : !!profile.alfa_tester;
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [selectedBiddings, setSelectedBiddings] = useState<Array<number>>([]);
  const [token, setToken] = useState<string | null>(null);
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [customSearches, setCustomSearches] = useState<CustomSearches[]>([]);
  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [allSelected, setAllSelected] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState<boolean>(false);
  const [batchRequestedCount, setBatchRequestedCount] = useState<number>(0);
  const [batchRemovedCount, setBatchRemovedCount] = useState<number | null>(null);
  const [batchErrorMessage, setBatchErrorMessage] = useState<string | undefined>(undefined);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Array<number> | null>(null);

  const buildSuccessMessage = (requested: number, removed: number | null) => {
    const effectiveRemoved = removed ?? requested;
    const favoritedBy = isCompanyView ? 'pelos membros escolhidos' : 'por você';

    if (effectiveRemoved <= 0) {
      return isCompanyView
        ? 'Nenhuma licitação favoritada pelos membros escolhidos foi desfavoritada.'
        : 'Nenhuma licitação sua foi desfavoritada.';
    }

    const ownedLabel =
      effectiveRemoved === 1
        ? `Licitação favoritada ${favoritedBy} foi desfavoritada!`
        : `${effectiveRemoved} licitações favoritadas ${favoritedBy} foram desfavoritadas!`;

    if (removed !== null && removed < requested) {
      const skipped = requested - removed;
      const skippedLabel = isCompanyView
        ? skipped === 1
          ? 'não é favoritada pelos membros escolhidos e foi mantida'
          : 'não são favoritadas pelos membros escolhidos e foram mantidas'
        : skipped === 1
        ? 'pertencia apenas a outro membro e foi mantida'
        : 'pertenciam apenas a outros membros e foram mantidas';
      return `${ownedLabel} ${skipped} ${skippedLabel}.`;
    }

    return ownedLabel;
  };

  const toggleSelectedBiddings = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      selectedList: selectedBiddings,
      setSelectedList: setSelectedBiddings,
      sourceList: biddings?.bidding_list,
      wholePageSelected,
      updateAll,
    });
  };

  const handlePostsPerPage = async (value: number | undefined) => {
    if (!value) return;

    dispatch({ type: OnlineSearchTypes.UPDATE_POSTS_PER_PAGE, postsPerPage: value });
    dispatch({ type: OnlineSearchTypes.UPDATE_CURRENT_PAGE, currentPage: 1 });
  };

  const handleOrderFieldChange = (selected: OrderOptionType | null) => {
    if (!selected) return;
    dispatch({
      type: OnlineSearchTypes.HANDLE_ORDER,
      orderedBy: selected.value,
      orderDirection: selected.value === 'validade' ? 'asc' : 'desc',
    });
  };

  const handleOrderDirectionChange = (orderDirection: 'asc' | 'desc') => {
    dispatch({
      type: OnlineSearchTypes.HANDLE_ORDER,
      orderedBy: onlineSearch.orderedBy || 'modified',
      orderDirection,
    });
  };

  const selectedOrderField =
    ORDER_FIELD_OPTIONS.find((option) => option.value === (onlineSearch.orderedBy || 'modified')) ||
    ORDER_FIELD_OPTIONS[0];

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    windowDiv?.addEventListener('scroll', () => {
      setScrollToolsClass(windowDiv?.scrollTop > 10 ? 'border-bottom' : '');
    });
  }, []);

  useEffect(() => {
    if (!biddings?.bidding_list) return;

    setWholePageSelected(
      biddings?.bidding_list?.length > 0 &&
        biddings?.bidding_list
          ?.map((bidding) => bidding.id)
          .filter((bidding) => selectedBiddings.includes(bidding))?.length ===
          biddings?.bidding_list?.length,
    );
  }, [
    selectedBiddings,
    onlineSearch.currentPage,
    onlineSearch.postsPerPage,
    biddings?.bidding_list,
  ]);

  useEffect(() => {
    const get_custom_searches = async () => {
      const response = await getCustomSearch(profile.id);
      if (response) {
        setCustomSearches(response);
      }
    };

    get_custom_searches();
  }, [biddings]);

  const perPageOptions = [
    { value: 20, label: 20 },
    { value: 40, label: 40 },
    { value: 60, label: 60 },
    { value: 80, label: 80 },
    { value: 100, label: 100 },
  ];

  const downloadFile = (fileExtension: string) => {
    getDownloadBiddings(
      fileExtension,
      'licitacoes',
      selectedBiddings,
      selectedBiddings?.length,
      companyId(profile),
      false,
    );
  };

  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const updateCurrentPage = (currentPage = 1) => {
    dispatch({ type: OnlineSearchTypes.UPDATE_CURRENT_PAGE, currentPage });
  };

  const clearSelected = () => {
    setSelectedBiddings([]);
    setAllSelected(false);
  };

  const unfavoriteBiddings = () => {
    createTrack('Clicou em Remover dos favoritos');
    if (selectedBiddings.length > 0) {
      dispatch(
        destroyFavoriteAll(
          selectedBiddings,
          profile.contact_name || profile.name,
          profile?.currentCompany[tabId()],
          allSelected,
          fromManagement,
          management?.filtered_by,
        ),
      );
      setSelectedBiddings([]);
    }
  };

  const effectiveDeleteCount = allSelected
    ? management?.management_summary?.total_favorites || selectedBiddings.length
    : selectedBiddings.length;

  const openBatchModal = () => {
    setSelectedMemberIds(null);
    setBatchErrorMessage(undefined);
    if (isCompanyView && management.members?.companyId !== companyId(profile)) {
      dispatch(getManagementMembers(companyId(profile)));
    }
    setShowBatchDeleteModal(true);
  };

  const favoritedNames = (biddings?.bidding_list || [])
    .filter((bidding) => selectedBiddings.includes(bidding.id))
    .flatMap((bidding) => bidding.favorited_by || []);
  const favoritedMemberIds =
    management.members?.companyId === companyId(profile)
      ? [...management.members.active, ...management.members.inactive]
          .filter((member) => favoritedNames.includes(member.nome))
          .map((member) => member.id)
      : [];
  const affectedMemberIds = selectedMemberIds ?? favoritedMemberIds;

  const selectedMembers = [
    ...(management.members?.active || []),
    ...(management.members?.inactive || []),
  ].filter((member) => affectedMemberIds.includes(member.id));

  const membersSelect = isCompanyView ? (
    <AffectedMembersSelect
      active={management.members?.active || []}
      inactive={management.members?.inactive || []}
      value={affectedMemberIds}
      onChange={setSelectedMemberIds}
      currentUserId={profile.id}
      isLoading={!!managementRequest.get_management_members?.loading}
    />
  ) : null;

  return (
    <Container>
      <div
        className={`result-container col-12 ${
          searchBarOpened ? 'col-lg-9 offset-lg-3 pr-md-0' : 'col-lg-12 p-0'
        }`}
      >
        {profile.enable_captcha && showCaptcha === true && token === null && (
          <div className="w-100 d-flex justify-content-center mt-5">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Lcex2sdAAAAAKMks7alzUBCyRFy88kgqM07DcwW"
              onChange={(recaptchaToken: React.SetStateAction<string | null>) => {
                setShowCaptcha(false);
                setToken(recaptchaToken);
              }}
              hl="pt-BR"
            />
          </div>
        )}
        {profile.enable_captcha && showCaptcha === false && token === null && (
          <div className="d-flex justify-content-center mt-5">
            <button
              type="button"
              className="btn btn-success btn-sm mr-1"
              onClick={() => {
                setShowCaptcha(true);
              }}
              disabled={showCaptcha}
            >
              Ver Licitações
            </button>
          </div>
        )}
        {request?.get_biddings?.loading && (!profile.enable_captcha || token !== null) && (
          <div className="d-flex align-items-center justify-content-center loading-container">
            <Loader />
          </div>
        )}
        {request?.get_biddings?.loaded &&
          (!profile.enable_captcha || token !== null) &&
          (!profile.in_analysis || (profile.in_analysis && onlineSearch.currentPage === 1)) &&
          biddings?.bidding_list?.length > 0 && (
            <div className="row">
              <div className={`col-12 mx-auto ${!fromManagement ? 'col-lg-11 p-md-0' : 'px-4'}`}>
                {!fromManagement && (
                  <>
                    <div className="d-md-flex d-block align-items-center mt-4">
                      <h4 className="mb-0 font-weight-bold">Encontrar Licitações</h4>
                      <OpenTutorial type="modal" source="biddings" />
                    </div>
                    <div className="mt-4">
                      {customSearches.length > 0 && (
                        <CustomSearchesItem searches={customSearches} />
                      )}
                    </div>
                  </>
                )}
                {(fromManagement && management.filtered_by === 'contact_client') ||
                (fromManagement && !!profile.alfa_tester) ||
                (fromManagement && canShowDeleteButton) ? (
                  <div className={`row list-tools py-1 ${scrollToolsClass}`}>
                    <div className="position-relative col-lg-6 d-block py-3 px-2">
                      <div className="d-flex">
                        <SelectButton
                          isChecked={wholePageSelected}
                          id={0}
                          toggleSelected={toggleSelectedBiddings}
                          text="Selecionar página"
                        />
                        {selectedBiddings?.length > 0 &&
                          management.filtered_by === 'contact_client' &&
                          !canShowDeleteButton && (
                            <button
                              type="button"
                              onClick={unfavoriteBiddings}
                              className="btn btn-link remove-favorites d-flex align-items-center"
                            >
                              <span className="material-icons">delete</span>Remover dos favoritos
                            </button>
                          )}
                      </div>
                      <div />
                    </div>
                    <ItemTools
                      enabledButtons={selectedBiddings?.length > 0}
                      disabledPopoverLabel="a licitação"
                      downloadFile={downloadFile}
                      handleShare={handleShare}
                      handlePrint={handlePrint}
                      showDeleteButton={fromManagement && canShowDeleteButton}
                      handleDelete={
                        fromManagement && canShowDeleteButton ? openBatchModal : undefined
                      }
                    />
                    {showShareWindow && (
                      <ItemShare
                        setShowShareWindow={setShowShareWindow}
                        selectedItems={selectedBiddings}
                      />
                    )}
                    {selectedBiddings?.length > 0 && (
                      <div className="col-md-12">
                        <p className="itemToolsSelected px-3 ml-2">
                          {allSelected
                            ? `Todas as ${management?.management_summary?.total_favorites} licitações favoritadas estão selecionadas.`
                            : wholePageSelected
                            ? `Todas as ${biddings?.bidding_list?.length} licitações nesta página estão selecionadas.`
                            : `${selectedBiddings?.length} ${
                                selectedBiddings?.length === 1
                                  ? 'registro selecionado.'
                                  : 'registros selecionados.'
                              }`}
                          {!allSelected ? (
                            <button
                              type="button"
                              onClick={() => setAllSelected(!allSelected)}
                              className="btn btn-link p-0 pl-3 tour-remove-favorites-select-all-link"
                            >
                              Selecionar todas as {management.management_summary.total_favorites}{' '}
                              licitações favoritadas
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-link m-0 py-0 px-1 border-0 pl-2"
                              onClick={clearSelected}
                            >
                              Limpar seleção.
                            </button>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`row list-tools py-1 ${scrollToolsClass}`}>
                    <div className="position-relative col-lg-6 d-block py-3 px-2">
                      <div className="d-flex align-items-center">
                        <SelectButton
                          isChecked={wholePageSelected}
                          id={0}
                          toggleSelected={toggleSelectedBiddings}
                          text="Selecionar página"
                        />
                        {!fromManagement && selectedBiddings?.length > 0 && (
                          <button
                            type="button"
                            onClick={unfavoriteBiddings}
                            className="btn btn-link remove-favorites d-flex align-items-center"
                          >
                            <span className="material-icons">delete</span>Remover dos favoritos
                          </button>
                        )}
                      </div>
                      {selectedBiddings?.length > 0 && (
                        <div className="itemToolsSelected ml-3">
                          {selectedBiddings?.length}{' '}
                          {selectedBiddings?.length === 1
                            ? 'registro selecionado.'
                            : 'registros selecionados.'}
                          <button
                            type="button"
                            className="btn btn-link m-0 py-0 px-1 border-0"
                            onClick={() => setSelectedBiddings([])}
                          >
                            Limpar seleção.
                          </button>
                        </div>
                      )}
                    </div>
                    <ItemTools
                      enabledButtons={selectedBiddings?.length > 0}
                      disabledPopoverLabel="a licitação"
                      downloadFile={downloadFile}
                      handleShare={handleShare}
                      handlePrint={handlePrint}
                      showDeleteButton={fromManagement && canShowDeleteButton}
                      handleDelete={
                        fromManagement && canShowDeleteButton ? openBatchModal : undefined
                      }
                    />
                    {showShareWindow && (
                      <ItemShare
                        setShowShareWindow={setShowShareWindow}
                        selectedItems={selectedBiddings}
                      />
                    )}
                  </div>
                )}
                {fromManagement && canShowDeleteButton && showBatchDeleteModal && (
                  <BatchConfirmModal
                    modalOpened={showBatchDeleteModal}
                    setModalOpened={setShowBatchDeleteModal}
                    title="Remover favoritos"
                    message={
                      effectiveDeleteCount === 1
                        ? 'Tem certeza que deseja remover esta licitação dos favoritos?'
                        : `Tem certeza que deseja remover ${effectiveDeleteCount} licitações dos favoritos?`
                    }
                    confirmLabel={
                      effectiveDeleteCount === 1
                        ? 'Remover dos Favoritos'
                        : `Remover ${effectiveDeleteCount} dos Favoritos`
                    }
                    successMessage={buildSuccessMessage(batchRequestedCount, batchRemovedCount)}
                    errorMessage={
                      batchErrorMessage ||
                      'Você não pode remover licitações favoritadas de outros membros.'
                    }
                    variant="danger"
                    isLoading={request.destroy_favorite_bidding.loading}
                    isLoaded={request.destroy_favorite_bidding.loaded}
                    isError={request.destroy_favorite_bidding.error}
                    confirmBlocked={isCompanyView && affectedMemberIds.length === 0}
                    onConfirm={() => {
                      createTrack('Gestão de Licitações - Removeu favoritos em lote');
                      setBatchRequestedCount(
                        allSelected
                          ? management?.management_summary?.total_favorites ||
                              selectedBiddings.length
                          : selectedBiddings.length,
                      );
                      setBatchRemovedCount(null);
                      setBatchErrorMessage(undefined);
                      setSelectedMemberIds(affectedMemberIds);
                      const result: any = dispatch(
                        destroyFavoriteAll(
                          selectedBiddings,
                          profile.contact_name || profile.name,
                          profile?.currentCompany[tabId()],
                          allSelected,
                          fromManagement,
                          management?.filtered_by,
                          isCompanyView ? selectedMembers : undefined,
                        ),
                      );
                      if (result && typeof result.then === 'function') {
                        result.then((response: any) => {
                          if (response?.status === 404 && isCompanyView) {
                            setBatchErrorMessage(NO_MATCH_MESSAGE);
                          }
                          const count = response?.data?.removed_count;
                          const ids = response?.data?.removed_bidding_ids;
                          const deletedAt = response?.data?.deleted_at;
                          if (typeof count === 'number') setBatchRemovedCount(count);
                          if (Array.isArray(ids) && ids.length > 0 && deletedAt) {
                            const message =
                              ids.length === 1
                                ? '1 favorito removido.'
                                : `${ids.length} favoritos removidos.`;
                            dispatch(
                              showUndoToast({
                                type: 'favorites',
                                ids,
                                deletedAt,
                                message,
                                scope: management?.filtered_by,
                                companyId: profile?.currentCompany[tabId()],
                              }),
                            );
                          }
                        });
                      }
                    }}
                    onSuccess={clearSelected}
                  >
                    {membersSelect}
                  </BatchConfirmModal>
                )}
                {profile.in_analysis && <InAnalysisAlert />}
                {!fromManagement && (
                  <div className="row mb-4">
                    {profile.alfa_tester ? (
                      <>
                        <div className="col-6 d-flex align-items-center flex-wrap">
                          <div className="mr-3">
                            <label className="m-0" htmlFor="OrderBySearch">
                              Ordenar por
                            </label>
                            <div style={{ width: 190 }}>
                              <Select
                                name="OrderBySearch"
                                inputId="OrderBySearch"
                                closeMenuOnSelect
                                value={selectedOrderField}
                                onChange={(selected) =>
                                  handleOrderFieldChange(selected as OrderOptionType)
                                }
                                options={ORDER_FIELD_OPTIONS}
                              />
                            </div>
                            <div className="mt-1">
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="orderDirectionOptions"
                                  id="orderDirectionDefault"
                                  checked={
                                    selectedOrderField.value === 'validade'
                                      ? (onlineSearch.orderDirection || 'desc') === 'asc'
                                      : (onlineSearch.orderDirection || 'desc') === 'desc'
                                  }
                                  onChange={() =>
                                    handleOrderDirectionChange(
                                      selectedOrderField.value === 'validade' ? 'asc' : 'desc',
                                    )
                                  }
                                />
                                <label className="form-check-label" htmlFor="orderDirectionDefault">
                                  {selectedOrderField.value === 'validade'
                                    ? 'Mais próxima'
                                    : 'Mais recente'}
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="orderDirectionOptions"
                                  id="orderDirectionInverse"
                                  checked={
                                    selectedOrderField.value === 'validade'
                                      ? (onlineSearch.orderDirection || 'desc') === 'desc'
                                      : (onlineSearch.orderDirection || 'desc') === 'asc'
                                  }
                                  onChange={() =>
                                    handleOrderDirectionChange(
                                      selectedOrderField.value === 'validade' ? 'desc' : 'asc',
                                    )
                                  }
                                />
                                <label className="form-check-label" htmlFor="orderDirectionInverse">
                                  {selectedOrderField.value === 'validade'
                                    ? 'Mais distante'
                                    : 'Mais antiga'}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 d-flex flex-column align-items-end justify-content-center">
                          <PerPageOptions
                            options={perPageOptions}
                            postsPerPage={onlineSearch.postsPerPage}
                            handleSelect={handlePostsPerPage}
                          />
                          <div className="text-secondary">
                            <TotalEntriesLabel
                              itemLabel="licitações"
                              totalEntries={biddings.total_entries}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-6">
                          <PerPageOptions
                            options={perPageOptions}
                            postsPerPage={onlineSearch.postsPerPage}
                            handleSelect={handlePostsPerPage}
                          />
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-end text-secondary">
                          <TotalEntriesLabel
                            itemLabel="licitações"
                            totalEntries={biddings.total_entries}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                {fromManagement && (
                  <div className="row mt-2">
                    <PerPageOptions
                      options={perPageOptions}
                      postsPerPage={onlineSearch.postsPerPage}
                      handleSelect={handlePostsPerPage}
                    />
                  </div>
                )}
                <div ref={componentRef} className="row mx-0 mt-5">
                  <div className="col-12">
                    {request?.get_biddings?.loaded &&
                      biddings?.bidding_list?.map((bidding, index) => (
                        <BiddingItemNew
                          key={bidding.id}
                          bidding={bidding}
                          biddingIndex={
                            index + (onlineSearch.currentPage - 1) * onlineSearch.postsPerPage
                          }
                          isChecked={selectedBiddings.includes(bidding.id)}
                          toggleSelected={toggleSelectedBiddings}
                          source={fromManagement ? 'favorited' : 'biddings'}
                          showMatch={!!profile?.alfa_tester && !fromManagement}
                          matchActions={false}
                        />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        {request?.get_biddings?.error && (
          <EmptyResult title="Desculpe!" description="Algo deu errado, tente novamente." />
        )}
        {request?.get_biddings?.loaded &&
          (!profile.enable_captcha || token !== null) &&
          (!profile.in_analysis || (profile.in_analysis && onlineSearch.currentPage === 1)) &&
          biddings?.bidding_list?.length === 0 && (
            <EmptyResult
              title={
                !fromManagement
                  ? 'Desculpe!'
                  : 'Você e sua equipe ainda não favoritaram nenhuma licitação.'
              }
              description={
                !fromManagement
                  ? 'Não temos licitações que correspondam a esses filtros.'
                  : [
                      'Acesse seus ',
                      <a href="/boletim_web/public/boletins">Boletins de Licitações</a>,
                      ' ou ',
                      <a href="/banco_de_dados">Encontrar Licitações</a>,
                      ' para favoritar uma licitação',
                    ]
              }
            />
          )}
        {request?.get_biddings?.loaded && profile.in_analysis && onlineSearch.currentPage !== 1 && (
          <div className="alert alert-warning">
            Estamos finalizando a liberação do seu cadastro, aguarde que em breve todos os avisos de
            licitação estarão disponíveis para você.
          </div>
        )}
        {request?.get_biddings?.loaded && biddings.total_entries !== 0 && (
          <div className="row d-print-none">
            <div
              className={`col-12 mx-auto mb-5 pb-5 ${
                !fromManagement ? 'col-lg-11 p-md-0' : 'px-4'
              }`}
            >
              <Pagination
                postsPerPage={onlineSearch.postsPerPage}
                totalPosts={biddings.total_entries}
                paginate={updateCurrentPage}
                currentPage={onlineSearch.currentPage || 1}
                firstAndLastPage
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default BiddingList;
